import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const NumIn = ({ value, onChange, testid, cls, disabled }) => (
  <input type="number" inputMode="numeric" dir="ltr" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
    className={`w-full rounded-xl border-2 px-2 py-1.5 text-center font-black text-sm outline-none disabled:opacity-30 ${cls}`} />
);

const RuleRow = ({ r, set }) => {
  const reached = Number(r.target_units) > 0 && r.sold_units >= Number(r.target_units);
  return (
    <div className={`grid grid-cols-[1fr_80px_80px_70px] gap-2 items-center rounded-xl px-2 py-1 ${r.enabled ? "bg-blue-50/60" : ""}`} data-testid={`brand-rule-${r.brand_id}`}>
      <label className="flex items-center gap-2 min-w-0 cursor-pointer">
        <input type="checkbox" checked={r.enabled} onChange={(e) => set(r.brand_id, "enabled", e.target.checked)}
          data-testid={`brand-pick-${r.brand_id}`} className="w-5 h-5 accent-blue-700 shrink-0" />
        <span className={`font-bold text-sm truncate ${r.enabled ? "text-[#0B1E4A]" : "text-slate-400"}`}>{r.name}</span>
      </label>
      <NumIn value={r.profit_per_unit} disabled={!r.enabled} onChange={(v) => set(r.brand_id, "profit_per_unit", v)} testid={`brand-rate-${r.brand_id}`} cls="border-blue-200 text-blue-800" />
      <NumIn value={r.target_units} disabled={!r.enabled} onChange={(v) => set(r.brand_id, "target_units", v)} testid={`brand-target-${r.brand_id}`} cls="border-amber-200 text-amber-700" />
      <span className={`rounded-xl py-1.5 text-center font-black text-sm text-white ${!r.enabled ? "bg-slate-300" : reached ? "bg-emerald-500" : "bg-red-500"}`} dir="ltr" data-testid={`brand-sold-${r.brand_id}`}>{fmt(r.sold_units)}</span>
    </div>
  );
};

const BrandRulesModal = ({ mandoob, onClose }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/mandoobs/${mandoob.id}/brand-rules`).then(({ data }) => setRows(data.map((r) => ({ ...r, first: r.enabled })))).catch(() => toast.error("فشل التحميل"));
  }, [mandoob.id]);
  const set = (bid, k, v) => setRows((rs) => rs.map((r) => (r.brand_id === bid ? { ...r, [k]: v } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const rules = Object.fromEntries(rows.filter((r) => r.enabled).map((r) => [r.brand_id, { enabled: true, profit_per_unit: r.profit_per_unit, target_units: r.target_units }]));
      await axios.put(`${API}/admin/mandoobs/${mandoob.id}/brand-rules`, { rules });
      toast.success("تم حفظ نسب الشركات"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); } finally { setBusy(false); }
  };
  const shown = (rows || []).filter((r) => (r.name || "").includes(q.trim())).sort((a, b) => b.first - a.first);
  const picked = (rows || []).filter((r) => r.enabled).length;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" dir="rtl" data-testid="brand-rules-modal">
      <div className="bg-white w-full sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden">
        <div className="p-4 flex items-center justify-between text-white" style={{ background: "linear-gradient(135deg,#1E3A8A,#0B1E4A)" }}>
          <div><p className="font-black">نسب وتارجت الشركات</p><p className="text-[11px] opacity-80">{mandoob.full_name} · مختار {picked} شركة · يتصفّر بس من الإدارة</p></div>
          <button onClick={onClose} data-testid="brand-rules-close" className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 pt-3 relative">
          <Search className="w-4 h-4 absolute right-7 top-1/2 mt-1.5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن شركة..." data-testid="brand-rules-search"
            className="w-full rounded-xl border-2 border-slate-200 pr-9 pl-3 py-2 text-sm outline-none focus:border-blue-400" />
        </div>
        <div className="grid grid-cols-[1fr_80px_80px_70px] gap-2 px-6 pt-3 text-[10px] font-black text-slate-500">
          <span>الشركة (أشّر حتى تختارها)</span><span className="text-center">ربح/كرتون</span><span className="text-center">التارجت</span><span className="text-center">المبيوع</span>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1.5">
          {!rows ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-700" /> : shown.map((r) => <RuleRow key={r.brand_id} r={r} set={set} />)}
        </div>
        <div className="p-4 border-t">
          <button onClick={save} disabled={busy || !rows} data-testid="brand-rules-save"
            className="w-full bg-[#1E3A8A] text-white font-black rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} حفظ
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandRulesModal;
